'use client';
import {cn} from '@/lib/utils';
import {StrapiMenuItem} from '@/types/strapi';
import {ReactNode, useEffect, useState} from 'react';
import {countDown} from './helpers/count-down';
import {observeFade} from './helpers/observe-fade';
import IModal from './modules/IModal';
import MediaPreview from './modules/MediaPreview';
import SliderBase from './modules/SliderBase';
import Url from './modules/Url';
import DefaultTemplate from './template/default';
type Props = {
  children?: ReactNode;
  sideMenu?: {title?: string; content?: StrapiMenuItem[]};
  banners?: {
    image: string;
    mobile?: string;
    url?: string;
    caption?: string;
  }[];
  mobileBanners?: string[];
  bannerOptions?: {
    caption?: string[];
    url?: string[];
  };
  title?: string;
};

export default function PageLayout({
  children,
  title = '',
  sideMenu = {},
  banners = []
}: Props) {
  const [isLoading, setIsLoading] = useState(true);
  let trial = 0;
  let lastY = 0;
  useEffect(() => {
    countDown();
    observeFade();

    // if get param has #item-xxx, scroll to that item
    const hash = window.location.hash;
    const scrollToHash = () => {
      trial++;
      if (trial < 20) {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const top =
            element.getBoundingClientRect().top + window.scrollY - 150;
          window.scrollTo({top: top, behavior: 'instant'});
          if (lastY == window.pageYOffset) {
            setIsLoading(false);
            return;
          }
          lastY = window.pageYOffset;
        }
        setTimeout(() => {
          scrollToHash();
        }, 100);
      } else {
        setIsLoading(false);
      }
    };

    if (hash) {
      scrollToHash();
    } else {
      setIsLoading(false);
    }
  }, []);

  const fonts = [
    'family=Abhaya+Libre:wght@400;500;600;700;800',
    'family=Noto+Sans+HK:wght@100..900',
    'family=Open+Sans:ital,wght@0,300..800;1,300..800',
    'family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700',
    'family=Questrial',
    'family=Qwigley',
    'family=Afacad:ital,wght@0,400..700;1,400..700',
    'family=Abril+Fatface',
    'family=Alexandria:wght@100..900',
  ];

  return (
    <div
      className={cn(
        'flex grow flex-col transition-all ',
        isLoading ? 'opacity-0 -transform-y-4' : 'opacity-100 transform-y-0'
      )}
    >
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" />
      <link
        rel="stylesheet"
        href={`https://fonts.googleapis.com/css2?${fonts.join('&')}&display=swap`}
      ></link>

      <MediaPreview />
      <IModal />

      <div
        className={cn(
          'relative w-full bg-white',
          banners.length > 0 && 'mb-7'
        )}
      >
        <SliderBase
          items={banners}
          options={{withDots: true, fullWidth: true, basis: 'none'}}
          template={(banner) => (
            <Url
              href={banner?.url || '#'}
              className={cn(
                banner?.url && banner?.url !== '#'
                  ? 'cursor-pointer'
                  : 'cursor-default'
              )}
            >
              <div className="flex flex-col items-center justify-center text-white ">
                <>
                  <div className="hidden w-full overflow-hidden md:flex justify-center items-center max-h-[60vh]">
                    <img
                      className="hidden md:block object-cover object-center "
                      src={banner.image}
                      alt={`${banner.caption || title} image`}
                      style={{
                        height: 'auto',
                        width: '100%'
                      }}
                    />
                  </div>
                </>

                <div className="block md:hidden">
                  <img
                    className="object-cover object-center max-w-[100vw] aspect-[16/9]"
                    src={
                      banner.mobile && banner.mobile != ''
                        ? banner.mobile
                        : banner.image
                    }
                    alt={`${banner.caption || title} image`}
                  />
                </div>

                {banner.caption && (
                  <div
                    className={cn(
                      'px-4 py-5 text-xl container flex items-start  font-500 overflow-hidden ',
                      'justify-start md:text-2xl ',
                      'lg:px-0 '
                    )}
                  >
                    <div className="line-clamp-4 md:line-clamp-2">
                      {banner.caption}
                    </div>
                  </div>
                )}
              </div>
            </Url>
          )}
        ></SliderBase>
        <div className="absolute bottom-[30%] right-0 left-0 sm:right-[20%] sm:left-[unset] flex justify-center">
          <h1
            className="text-white text-[6vw] sm:text-[48px] font-[900] px-4"
            style={{textShadow: '1px 3px #000'}}
          >
            {title}
          </h1>
        </div>
      </div>

      <div>
        <DefaultTemplate>{children}</DefaultTemplate>
      </div>
    </div>
  );
}
